import { useEffect, useState } from "react";
import { Box, Button, Stack } from "@mui/material";

import Loader from "../Loader";
import EDiaryConfigSection, { validateEdiaryConfig } from "./EDiaryConfigSection";
import useGetEdiaryConfig from "../../hooks/useGetEdiaryConfig";
import useUpdateEdiaryConfigMutation from "../../hooks/useUpdateEdiaryConfigMutation";
import { EDIARY_CONFIG_ROLES } from "../../utils/constants";

const EDITABLE_FIELDS = [
  "name",
  "name_de",
  "name_es",
  "interval_hours",
  "occurrence_count",
  "window_hours",
  "allow_investigator_start_date_edit",
];

const pickEditable = (config) =>
  Object.fromEntries(EDITABLE_FIELDS.map((field) => [field, config?.[field] ?? ""]));

const toPayload = (config) => ({
  ...pickEditable(config),
  interval_hours: Number(config.interval_hours),
  occurrence_count: Number(config.occurrence_count),
  window_hours: Number(config.window_hours),
  allow_investigator_start_date_edit: Boolean(config.allow_investigator_start_date_edit),
});

const EDiaryTab = ({ trialId, isLocked = false }) => {
  const role = JSON.parse(localStorage.getItem("user_data"))?.role;
  const canEdit = EDIARY_CONFIG_ROLES.includes(role) && !isLocked;

  const { data, isLoading } = useGetEdiaryConfig({ trialId });
  const updateConfig = useUpdateEdiaryConfigMutation();

  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  if (isLoading || !draft) return <Loader />;

  const hasChanged = JSON.stringify(pickEditable(draft)) !== JSON.stringify(pickEditable(data));
  const validationError = validateEdiaryConfig(draft);

  const handleSave = () => updateConfig.mutate({ trialId, config: toPayload(draft) });

  return (
    <Box sx={{ mt: 2 }} data-testid="ediary-tab">
      {canEdit && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            data-testid="ediary-save-btn"
            disabled={!hasChanged || Boolean(validationError) || updateConfig.isLoading}
          >
            Save eDiary
          </Button>
        </Stack>
      )}

      <EDiaryConfigSection config={draft} onChange={setDraft} role={role} isLocked={!canEdit} />
    </Box>
  );
};

export default EDiaryTab;
